/* Iter252 · Sunbiz Timeline Widget
   ---------------------------------------------------------------
   Renders annual reports + event history for a FL prospect as a
   compact vertical timeline. Signals compliance (last AR year vs
   current year) and continuity (event cadence).
   Data source: GET /api/smith/sunbiz/timeline/{pid}
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function SunbizTimelineWidget({ prospectId, prospectState, testid = "sunbiz-timeline" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (prospectState !== "FL") { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/smith/sunbiz/timeline/${prospectId}`);
        if (!cancelled) setData(data);
      } catch (_e) { if (!cancelled) setData(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [prospectId, prospectState]);

  if (prospectState !== "FL") return null;
  if (loading) {
    return (
      <div data-testid={`${testid}-loading`}
           className="rounded-xl border border-white/10 bg-black/30 p-3 text-[11.5px] text-slate-500">
        Carregando timeline Sunbiz…
      </div>
    );
  }

  if (!data || !data.ok) {
    return (
      <div data-testid={`${testid}-needs-enrich`}
           className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 flex items-center gap-2">
        <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
        <span className="text-[11.5px] text-amber-100">
          Sunbiz ainda não consultado. Clique <b>Enrich Sunbiz</b> no card
          <b> Company</b> pra ver o histórico de annual reports e eventos.
        </span>
      </div>
    );
  }

  const ars = data.annual_reports || [];
  const events = data.event_history || [];
  const currentYear = new Date().getFullYear();
  const lastAR = ars[0]?.year ? parseInt(ars[0].year, 10) : null;
  const isCompliant = lastAR && (currentYear - lastAR) <= 1;

  return (
    <div data-testid={testid}
         className="rounded-xl border border-white/10 p-3 sm:p-4"
         style={{ background: "linear-gradient(135deg,rgba(249,115,22,0.08) 0%,rgba(15,23,42,0.7) 60%)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-orange-400" />
          <h3 className="font-extrabold text-white text-[12.5px] uppercase tracking-wider">
            Sunbiz · Compliance Timeline
          </h3>
        </div>
        {data.detail_url && (
          <a href={data.detail_url} target="_blank" rel="noopener noreferrer"
             data-testid={`${testid}-external`}
             className="text-[10.5px] text-orange-300 hover:text-orange-200 inline-flex items-center gap-1">
            Sunbiz.org <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Compliance header stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <StatTile label="Status"
                  value={data.status_current || "—"}
                  color={data.status_current === "Active" ? "#10B981" : "#F59E0B"}
                  testid={`${testid}-status`} />
        <StatTile label="Último AR"
                  value={lastAR ? `${lastAR}` : "—"}
                  color={isCompliant ? "#10B981" : "#EF4444"}
                  icon={isCompliant ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  testid={`${testid}-lastar`} />
        <StatTile label="Filed"
                  value={data.date_filed || "—"}
                  color="#94A3B8"
                  testid={`${testid}-filed`} />
      </div>

      {/* Iter289 · BOI Compliance Tracker (FinCEN CTA) */}
      {data.boi && data.boi.status && data.boi.status !== "unknown" && (
        <div className="mb-3 rounded-md border p-2.5"
             data-testid={`${testid}-boi`}
             style={{
               background: data.boi.status === "overdue"
                            ? "rgba(239,68,68,0.10)"
                            : data.boi.status === "due_soon"
                              ? "rgba(245,158,11,0.10)"
                              : "rgba(16,185,129,0.08)",
               borderColor: data.boi.status === "overdue"
                            ? "rgba(239,68,68,0.35)"
                            : data.boi.status === "due_soon"
                              ? "rgba(245,158,11,0.35)"
                              : "rgba(16,185,129,0.30)",
             }}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5"
                style={{ color: data.boi.status === "overdue" ? "#EF4444"
                                : data.boi.status === "due_soon" ? "#F59E0B" : "#10B981" }} />
              <span className="text-[10.5px] uppercase tracking-widest font-black"
                    style={{ color: data.boi.status === "overdue" ? "#FCA5A5"
                                    : data.boi.status === "due_soon" ? "#FCD34D" : "#6EE7B7" }}>
                BOI · FinCEN
              </span>
              <span className="text-[9.5px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest"
                    style={{
                      background: data.boi.status === "overdue" ? "#EF4444"
                                   : data.boi.status === "due_soon" ? "#F59E0B"
                                   : data.boi.status === "not_covered" ? "#64748B" : "#10B981",
                      color: "#0F172A",
                    }}
                    data-testid={`${testid}-boi-status`}>
                {data.boi.status === "overdue" ? "Vencido"
                  : data.boi.status === "due_soon" ? "Vence em breve"
                  : data.boi.status === "not_covered" ? "Isento" : "Em dia"}
              </span>
            </div>
            {data.boi.deadline && (
              <span className="text-[10px] text-slate-300 tabular-nums" data-testid={`${testid}-boi-deadline`}>
                Prazo: <strong className="text-white">{data.boi.deadline}</strong>
                {typeof data.boi.days_left === "number" && (
                  <span className="ml-1 text-slate-400">
                    ({data.boi.days_left < 0 ? `${-data.boi.days_left}d atraso` : `${data.boi.days_left}d`})
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-slate-200/90" data-testid={`${testid}-boi-msg`}>
            {data.boi.message}
          </div>
          <a href="https://boiefiling.fincen.gov/" target="_blank" rel="noopener noreferrer"
             data-testid={`${testid}-boi-file-link`}
             className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-sky-300 hover:text-sky-100">
            Fazer/Verificar filing → boiefiling.fincen.gov <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      )}

      {/* Annual Reports timeline */}
      {ars.length > 0 && (
        <div className="mb-3" data-testid={`${testid}-ars`}>
          <div className="text-[9.5px] uppercase tracking-widest font-bold text-orange-300 mb-1.5">
            Annual Reports ({ars.length})
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {(expanded ? ars : ars.slice(0, 6)).map((r) => (
              <div key={r.year}
                   data-testid={`${testid}-ar-${r.year}`}
                   title={`Filed ${r.filed_date}`}
                   className="rounded-md bg-orange-500/15 border border-orange-500/25 px-2 py-1">
                <div className="text-[10.5px] font-extrabold text-orange-100 tabular-nums">
                  {r.year}
                </div>
                <div className="text-[9.5px] text-orange-300/80 tabular-nums">
                  {r.filed_date}
                </div>
              </div>
            ))}
            {ars.length > 6 && (
              <button type="button" onClick={() => setExpanded(!expanded)}
                      data-testid={`${testid}-toggle`}
                      className="text-[10.5px] text-orange-300 hover:text-orange-100 px-2 py-1 rounded-md border border-orange-500/25 hover:bg-orange-500/10">
                {expanded ? "− menos" : `+ ${ars.length - 6}`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Event history — most recent 5 filings */}
      {events.length > 0 && (
        <div data-testid={`${testid}-events`}>
          <div className="text-[9.5px] uppercase tracking-widest font-bold text-orange-300 mb-1.5">
            Recent Events ({events.length})
          </div>
          <ul className="space-y-1">
            {events.slice(0, 5).map((e, idx) => (
              <li key={`${e.date}-${idx}`}
                  data-testid={`${testid}-event-${idx}`}
                  className="flex items-start gap-2 text-[11px]">
                <span className="text-orange-300 tabular-nums shrink-0 w-[75px]">
                  {e.date}
                </span>
                <span className="text-slate-200">{e.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ars.length === 0 && events.length === 0 && (
        <div className="text-[11px] text-slate-400" data-testid={`${testid}-empty`}>
          Sunbiz respondeu, mas não encontrei annual reports ou eventos nesta entidade.
        </div>
      )}
    </div>
  );
}


function StatTile({ label, value, color, icon = null, testid }) {
  return (
    <div className="rounded-md bg-black/40 border border-white/10 px-2 py-1.5"
         data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">
        {label}
      </div>
      <div className="text-[12.5px] font-extrabold tabular-nums leading-tight inline-flex items-center gap-1"
           style={{ color }}>
        {icon}{value}
      </div>
    </div>
  );
}
